import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { checkAdminCredentials } from "./adminAuth";
import { BASE_URL, TRACKS, PACKAGES, formatNaira } from "./masterclassConfig";
import { callServer } from "./serverStatus";
import ServerWakeOverlay from "./components/ServerWakeOverlay";

const trackName = (id) => TRACKS.find((t) => t.id === id)?.name || id;
const packageName = (id) => PACKAGES.find((p) => p.id === id)?.name || id;
const formatDate = (d) =>
  d ? new Date(d).toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short", timeZone: "Africa/Lagos" }) : "";

const MasterclassAdmin = () => {
  const [isAdmin, setIsAdmin] = useState(() => {
    try {
      return Boolean(localStorage.getItem("isAdmin"));
    } catch {
      return false;
    }
  });
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [regs, setRegs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [paidFilter, setPaidFilter] = useState("all"); // all | paid | unpaid
  const [trackFilter, setTrackFilter] = useState("all");

  const fetchRegs = async () => {
    setIsLoading(true);
    try {
      const { data } = await callServer(
        () => axios.get(`${BASE_URL}/api/masterclass/registrations`),
        "Loading registrations…"
      );
      setRegs(data);
    } catch (error) {
      toast.error(error.message || "Couldn't load registrations.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchRegs();
  }, [isAdmin]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (checkAdminCredentials(creds.username, creds.password)) {
      try {
        localStorage.setItem("isAdmin", "true");
      } catch {}
      setIsAdmin(true);
    } else {
      toast.error("Invalid credentials!");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return regs.filter((r) => {
      if (paidFilter === "paid" && !r.isPaid) return false;
      if (paidFilter === "unpaid" && r.isPaid) return false;
      if (trackFilter !== "all" && r.track !== trackFilter) return false;
      if (!q) return true;
      return [r.fullName, r.email, r.whatsapp, r.paidReference].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [regs, search, paidFilter, trackFilter]);

  const stats = useMemo(() => {
    const paid = regs.filter((r) => r.isPaid);
    return {
      total: regs.length,
      paid: paid.length,
      revenue: paid.reduce((sum, r) => sum + (r.amountPaidKobo || r.amountKobo || 0), 0),
      byTrack: TRACKS.map((t) => ({ name: t.name, count: paid.filter((r) => r.track === t.id).length })),
      withRecording: paid.filter((r) => r.package === "live_recording").length,
    };
  }, [regs]);

  const handleDownload = () => {
    const paid = paidFilter === "paid" ? "true" : paidFilter === "unpaid" ? "false" : "";
    window.location.href = `${BASE_URL}/api/masterclass/registrations/download${paid ? `?paid=${paid}` : ""}`;
  };

  if (!isAdmin) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4">
        <form onSubmit={handleLogin} className="bg-white p-5 rounded shadow-md w-full max-w-sm">
          <h2 className="text-xl font-bold mb-4">Admin login</h2>
          <input
            type="text" placeholder="Username" className="border p-2 mb-2 w-full"
            onChange={(e) => setCreds({ ...creds, username: e.target.value })}
          />
          <input
            type="password" placeholder="Password" className="border p-2 mb-4 w-full"
            onChange={(e) => setCreds({ ...creds, password: e.target.value })}
          />
          <button type="submit" className="bg-[#F20034] text-white px-4 py-2 rounded w-full">Login</button>
        </form>
      </div>
    );
  }

  const Stat = ({ label, value }) => (
    <div className="bg-white border rounded-lg p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <ServerWakeOverlay />
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1 className="text-2xl font-bold">AI Masterclass registrations</h1>
        <div className="flex gap-3 text-sm">
          <button onClick={fetchRegs} className="underline text-gray-600">Refresh</button>
          <Link to="/admin/bootcamp" className="underline text-gray-600">Bootcamp archive →</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <Stat label="Registered" value={stats.total} />
        <Stat label="Paid" value={stats.paid} />
        <Stat label="Unpaid" value={stats.total - stats.paid} />
        <Stat label="Revenue" value={formatNaira(stats.revenue)} />
      </div>
      <p className="text-sm text-gray-600 mb-5">
        Paid by track: {stats.byTrack.map((t) => `${t.name} ${t.count}`).join(" · ")} · With recording:{" "}
        {stats.withRecording}
      </p>

      <div className="flex flex-col md:flex-row gap-2 mb-4">
        <input
          type="text" placeholder="Search name, email, phone, reference" className="border p-2 flex-1"
          value={search} onChange={(e) => setSearch(e.target.value)}
        />
        <select className="border p-2" value={paidFilter} onChange={(e) => setPaidFilter(e.target.value)}>
          <option value="all">All payments</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
        </select>
        <select className="border p-2" value={trackFilter} onChange={(e) => setTrackFilter(e.target.value)}>
          <option value="all">All tracks</option>
          {TRACKS.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <button onClick={handleDownload} className="bg-[#F20034] text-white px-4 py-2 rounded">
          Download Excel
        </button>
      </div>

      <div className="overflow-auto max-h-[650px] border border-gray-300 bg-white">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-white sticky top-0 shadow-sm">
            <tr>
              {["#", "Registered", "Name", "Email", "WhatsApp", "Track", "Package", "Amount", "Paid", "Email sent", "Paystack ref"].map(
                (h) => (
                  <th key={h} className="border border-gray-300 px-3 py-2 text-left whitespace-nowrap">{h}</th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading && regs.length === 0 ? (
              <tr><td colSpan={11} className="text-center p-4">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={11} className="text-center p-4 text-gray-500">No registrations yet.</td></tr>
            ) : (
              filtered.map((r, i) => (
                <tr key={r._id} className={r.isPaid ? "" : "bg-amber-50"}>
                  <td className="border border-gray-300 px-3 py-2">{i + 1}</td>
                  <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(r.createdAt)}</td>
                  <td className="border border-gray-300 px-3 py-2">{r.fullName}</td>
                  <td className="border border-gray-300 px-3 py-2">{r.email}</td>
                  <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{r.whatsapp}</td>
                  <td className="border border-gray-300 px-3 py-2">{trackName(r.track)}</td>
                  <td className="border border-gray-300 px-3 py-2">{packageName(r.package)}</td>
                  <td className="border border-gray-300 px-3 py-2">{formatNaira(r.amountPaidKobo || r.amountKobo)}</td>
                  <td className="border border-gray-300 px-3 py-2">{r.isPaid ? "✅ Yes" : "No"}</td>
                  <td className="border border-gray-300 px-3 py-2">{r.isPaid ? (r.confirmationEmailSent ? "Yes" : "Pending") : "—"}</td>
                  <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{r.paidReference || ""}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MasterclassAdmin;
